import React, { useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

export interface TooltipProps {
  label: string
  action?: string
  top?: string
  left?: string
  right?: string
  fixed?: boolean
  position?: 'top' | 'left' | 'right' | 'bottom'
  dataAttribute?: string
  reactIntlId?: string
  className?: string
  fixedToBody?: boolean
  topGap?: number
}

interface stylesInterfece {
  [key:string]: {
    [key:string]:string
  }
}

export const Tooltip: React.FC<TooltipProps> = ({ children, label, action = "default", top, left, right, fixed, position='top', dataAttribute, reactIntlId, className, fixedToBody = false, topGap }) => {

  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const [tooltipLeft, setTooltipLeft] = useState<string | null>(null)
  const [positionArrow, setPositionArrow] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if(fixed && tooltipRef.current){
        const { right } = tooltipRef.current?.getBoundingClientRect()
        if(right  >= document.body.offsetWidth){
          setTooltipLeft(`-${(right - document.body.offsetWidth).toFixed()}px`)
          setPositionArrow('right-[17px]')
        }
    }
  }, [fixed])

  // Handle portal positioning when fixedToBody is true
  useLayoutEffect(() => {
    if (fixedToBody && visible && tooltipRef.current && triggerRef.current) {
      const updatePosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;
        
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        let newTop = 0;
        let newLeft = 0;

        if (position === 'top') {
          if (topGap !== undefined) {
            newTop = triggerRect.top - tooltipRect.height - topGap;
          } else {
            // Legacy fixed offset, kept for existing consumers tuned around it
            const FIXED_TOP_OFFSET = 140;
            newTop = triggerRect.top - FIXED_TOP_OFFSET;
          }
          newLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          
          // Adjust horizontal position to stay within viewport
          const viewportWidth = window.innerWidth;
          const minLeft = 20;
          const maxLeft = viewportWidth - tooltipRect.width - 20;
          
          if (newLeft < minLeft) {
            newLeft = minLeft;
          } else if (newLeft > maxLeft) {
            newLeft = maxLeft;
          }
        } else if (position === 'left') {
          newTop = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newLeft = triggerRect.left - tooltipRect.width - 8;
        } else if (position === 'right') {
          newTop = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          newLeft = triggerRect.right + 8;
        } else if (position === 'bottom') {
          newTop = triggerRect.bottom + 8;
          newLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        }

        setTooltipPosition({ top: newTop, left: newLeft });
      };

      updatePosition();
      
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [fixedToBody, visible, position, topGap])
 
  const bodyStyles: stylesInterfece  = {
    top: {
      default: `text-white bg-black -translate-x-1/2 -translate-y-full ${left ? left : 'left-2/4'} ${top ? top : '-top-3'}`,
      danger: `text-danger bg-danger-light -translate-x-1/2 -translate-y-full ${left ? left : 'left-2/4'} ${top ? top : '-top-3'}` 
    },
    left: {
      default: `text-white bg-black -translate-x-9 ${left ? left : 'right-4'} ${top ? top : '-top-2'}`,
      danger: `text-danger bg-danger-light -translate-x-9 ${left ? left : 'right-4'} ${top ? top : '-top-2'}`
    },
    right: {
      default: `text-white bg-black  ${left ? left : 'left-[110%]'} ${top ? top : '-top-2'}`,
      danger: `text-danger bg-danger-light ${left ? left : 'left-[110%]'} ${top ? top : '-top-2'}`
    },
    bottom: {
      default: `text-white bg-black -translate-x-1/2 ${left ? left : 'left-2/4'} ${top ? top : 'top-3'}`,
      danger: `text-danger bg-danger-light -translate-x-1/2 ${left ? left : 'left-2/4'} ${top ? top : 'top-3'}`
    }
  }

  const arrowStyles: stylesInterfece = {
    top: {
      default: `after:bg-black ${positionArrow ? positionArrow : 'left-2/4'} after:-translate-x-2/4 top-full`,
      danger: `after:bg-danger-light ${positionArrow ? positionArrow : 'left-2/4'} after:-translate-x-2/4 top-full`
    },
    left: {
      default: `after:bg-black top-4 right-[-18px] -rotate-90 `,
      danger: `after:bg-danger-light top-4 right-[-18px] -rotate-90 ` 
    },
    right: {
      default: `after:bg-black top-[10px] left-[-6px] rotate-90 `,
      danger: `after:bg-danger-light top-4 right-[-18px] -rotate-90 ` 
    },
    bottom: {
      default: `after:bg-black bottom-full ${positionArrow ? positionArrow : 'left-2/4'} after:-translate-x-2/4 after:translate-y-2/4`,
      danger: `after:bg-danger-light bottom-full ${positionArrow ? positionArrow : 'left-2/4'} after:-translate-x-2/4 after:translate-y-2/4`
    }
  }

  // Tooltip content element
  const tooltipContent = (
    <div 
      ref={tooltipRef}
      {...(dataAttribute && {"data-attribute": `tooltip-${dataAttribute}`})}
      style={fixedToBody ? {
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        transform: 'none',
        zIndex: 1000001,
        pointerEvents: 'none'
      } : (fixed && tooltipLeft ? { left: tooltipLeft } : {})}
      className={`min-w-[80px] flex items-center  text-center py-2 px-4 ${
        fixedToBody 
          ? (visible ? 'flex' : 'hidden')
          : (!fixed ? 'hidden group-hover:flex' : 'block')
      } rounded w-max py-2 px-4 ${!label ? 'bg-transparent' : bodyStyles[position][action]} font-normal text-sm ${fixedToBody ? '' : 'absolute z-50'} whitespace-no-wrap ${className}`}
    >
      {
        reactIntlId ?
        <FormattedMessage id={reactIntlId} defaultMessage={label} />
        :
    label 
      }
      <i className={`absolute -ml-3 w-6 h-3 overflow-hidden after:absolute after:w-[6px] after:h-[6px] ${!label ? 'bg-transparent' : arrowStyles[position][action]} after:left-2/4 after:transform after:rotate-45 after:-translate-y-2/4`}></i>
    </div>
  );

  return (
    <div 
      ref={triggerRef}
      className={`relative text-left ${!fixed && !fixedToBody && 'group'}`}
      onMouseEnter={() => fixedToBody && setVisible(true)}
      onMouseLeave={() => fixedToBody && setVisible(false)}
    >
      {children}
      {fixedToBody ? createPortal(tooltipContent, document.body) : tooltipContent}
    </div>
  )
}